<template>
  <div class="how-to-wear">
    <h2 class="heading">How you're wearing it</h2>
    <div class="slider__wrapper">
      <div ref="slider" class="slider">
        <div
          v-for="(post, index) in posts"
          :key="post.id"
          ref="slides"
          class="slide"
        >
          <img
            v-if="post.type === 'IMAGE'"
            :src="post.url"
            class="image"
            :alt="post.caption"
          />

          <Video
            v-if="post.type === 'VIDEO'"
            :intersected="intersectStatus[index]"
            :video="{
              sources: [
                {
                  url: post.url,
                },
              ],
            }"
            @lowPower="lowPower = true"
          >
          </Video>

          <div class="info">
            <span class="likes">{{ post.like_count }} likes</span>
            <p class="caption">{{ post.caption }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="dots">
      <button
        v-for="(post, index) in dots"
        :key="post.id"
        class="dot"
        :class="{ 'dot--active': intersectStatus[index] }"
        @click="goToDot(index)"
      >
        <span class="dot__dot"></span>
      </button>
    </div>
  </div>
</template>

<script>
import Video from "@/components/Video.vue";

export default {
  name: "HowToWear",
  components: {
    Video,
  },
  props: {
    references: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeSlide: 1,
      observers: [],
      intersectStatus: [],
      lowPower: false,
    };
  },
  computed: {
    posts() {
      let posts = this.references;

      posts = posts.map((post) => {
        const fields = post.fields;
        const newPost = {};

        fields.forEach((field) => {
          if (field.type === "number_integer") {
            newPost[field.key] = parseInt(field.value).toLocaleString();
          } else {
            newPost[field.key] = field.value;
          }
        });

        return newPost;
      });

      if (this.lowPower) {
        posts = posts.filter((post) => {
          return post.type !== "VIDEO";
        });
      }

      return posts;
    },
    dots() {
      return this.posts.toSpliced(9);
    },
  },
  mounted() {
    this.intersectStatus = Array(this.posts.length).fill(false);

    // setup the observers for each gallery item
    this.$refs.slides.forEach((item, index) => {
      this.setupObserver(item, index);
    });
  },
  methods: {
    goToDot(index) {
      const galleryChildren = this.$refs.slider.children;

      galleryChildren[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    },
    setupObserver(element, index) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      };

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.activeSlide = index + 1;
          this.intersectStatus[index] = true;
        } else {
          this.intersectStatus[index] = false;
        }
      }, options);

      observer.observe(element);
      this.observers.push(observer);
    },
  },
};
</script>

<style lang="scss" scoped>
.how-to-wear {
  @include respond-above(md) {
    margin-top: 64px;
  }
}

.heading {
  @include t__editorial-headline-10;
  margin-bottom: 20px;

  @include respond-above(md) {
    @include t__editorial-headline-30;
    text-align: center;
    margin-bottom: 30px;
  }
}

.slider__wrapper {
  position: relative;
  margin-left: -$v-container-padding + 0px;
  margin-right: -$v-container-padding + 0px;
}

.slider {
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  position: relative;
  white-space: nowrap;
  text-align: center;

  &::-webkit-scrollbar {
    display: none;
  }

  & > .slide {
    scroll-snap-align: start;
    scroll-snap-stop: always;
    position: relative;
    text-align: left;
    display: inline-block;
    white-space: wrap;
    margin-right: 0;
    width: 85%;

    @include respond-above(md) {
      width: 25%;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 67.71%,
        rgba(0, 0, 0, 0.1) 76.56%,
        rgba(0, 0, 0, 0.36) 90.62%,
        rgba(0, 0, 0, 0.6) 100%
      );
    }
  }

  .image,
  ::v-deep(.video-intersection-observer),
  ::v-deep(.video__element) {
    display: block;
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: cover;
  }

  .info {
    position: absolute;
    left: 0;
    bottom: 0;
    display: block;
    padding: 15px;
    z-index: 2;
    right: 0;

    .likes {
      display: inline-block;
      color: white;
      @include t__utility-message-30;
      margin-bottom: 5px;
    }

    .caption {
      color: white;
      @include t__utility-body-10;
      margin-bottom: 5px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      -webkit-line-clamp: 2;
      text-overflow: ellipsis;
    }
  }
}

.dots {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;

  @include respond-above(md) {
    margin-top: 20px;
  }

  & > .dot {
    outline: none;
    border: none;
    background: none;
    padding: 5px;

    &.dot--active {
      .dot__dot {
        width: 4px;
        height: 4px;
      }
    }

    .dot__dot {
      display: block;
      background: $color-dark;
      width: 2px;
      height: 2px;
      border-radius: 50%;
    }
  }
}
</style>
